/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

SQC-562: Added MidFirstTransferPage route — teman.beck
*/

import { lazy, useMemo } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import useLazyComponent from '../common/hooks/useLazyComponent';
// import Login from '../pages/Login';
import Login from '../pages/LoginPage';
import LoginServicerPage from '../pages/LoginServicerPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import LeftNavBar from './LeftNavBar';
import Footer from './Footer';
import CreateAccountPage from '../pages/CreateAccountPage';
import AddStopCodePage from '../pages/AddStopCodePage';
import BulkDisbursementPage from '../pages/BulkDisbursementPage';
import AutopayPage from '../pages/AutopayPage';
import MyProfile from '../pages/MyProfile';
import LoanInformation from '../pages/LoanInformation';
import BorrowerPayHistory from '../pages/BorrowerPayHistory';
import Styles from '../css/MainContent.module.css';
import LoanDefaultCodesPage from '../pages/LoanDefaultCodesPage';
import LoanServicingEscrowAccrualsPage from '../pages/LoanServicingEscrowAccrualsPage';
import DocumentsPage from '../pages/DocumentsPage';
import { NavigationAlertProvider } from '../contexts/NavigationAlertContext';
import GlobalSnackbar from './GlobalSnackbar';
import ErrorPage from '../pages/ErrorPage';
import ExternalRedirect from './ExternalRedirect';
import GlossaryPage from '../pages/GlossaryPage';
import ChangeContactInfoPage from '../pages/ChangeContactInfoPage';
import OneTimePaymentPage from '../pages/OneTimePaymentPage';
import LoanHistoryPage from '../pages/LoanHistoryPage';
import LoanBuydownPage from '../pages/LoanBuydownPage';
import EscrowPaymentPage from '../pages/EscrowPaymentPage';
import CommunicationSkeleton from './skeletons/CommunicationSkeleton';
import ARMSettingsSkeleton from './skeletons/ARMSettingsSkeleton';
import LoanPaymentsDueSkeleton from './skeletons/LoanPaymentsDueSkeleton';
import PaymentsAndDisbursements from '../pages/PaymentsAndDisbursementsPage';
import SelectLoansPage from '../pages/SelectLoansPage';
import NoLoginRedirect from '../components/NoLoginRedirect';
import useKeyboardNavigator from '../common/hooks/useKeyboardNavigator';
import LoanInfoHeader from './LoanInfoHeader';
import EscrowAnalysisPage from '../pages/EscrowAnalysisPage';
import PullPayoffPage from '../pages/PullPayoffPage';
// import LoginServicerPageSkeleton from './skeletons/LoginServicerPageSkeleton';
import AddEditPayeePage from '../pages/AddEditPayeePage';
import GoGreenPage from '../pages/GoGreenPage';
import LoanSubsidyPage from '../pages/LoanSubsidyPage';
import { useAuthContext } from '../contexts/AuthContext';
import { useRootContext } from '../contexts/RootContext';
import NoPageFound from './NoPageFound';

import AmortizationSchedulePage from '../pages/AmortizationSchedulePage';
import PmiServicingPage from '../pages/PmiServicingPage';
import MipServicingPage from '../pages/MipServicingPage';
import MidFirstTransferPage from '../pages/MidFirstTransferPage';
import UploadPayeePage from '../pages/UploadPayeePage';
// import IRS1040TaxInfoPage from '../pages/IRS1040TaxInfoPage';
import TaxPaymentPage from '../pages/TaxPaymentsPage';
import { BorrowerEscrowAnalysisProvider } from '../contexts/BorrowerEscrowAnalysisContext';
import CreateCommentModal from './modals/CreateCommentModal';

import BorrowerLoginAuditPage from '../pages/BorrowerLoginAuditPage';
import { LoanInformationProvider } from '../contexts/LoanInformationContext';
import { LDCProvider } from '../contexts/LoanDefaultCodesContext';
import GNMAReportingPage from '../pages/GNMAReportingPage';
import ProctorImportReportsPage from '../pages/ProctorImportReportsPage';
import { ProctorImportReportsProvider } from '../contexts/ProctorImportReportsContext';
import LossDraftRepairPage from '../pages/LossDraftRepairPage';

const Communication = lazy(() => import('../pages/Communication'));
const LoanARM = lazy(() => import('../pages/LoanARM'));
const LoanPaymentsDue = lazy(() => import('../pages/LoanPaymentsDue'));

function MainContent() {
  const { navBarOpen, setNavBarOpen } = useRootContext();
  const { isKeyboardNavigator } = useKeyboardNavigator();
  const { pathname } = useLocation();

  const CommunicationProps = {};
  const CommunicationLazy = useLazyComponent(
    Communication,
    <CommunicationSkeleton />,
    `Communication: ${JSON.stringify(CommunicationProps)}`,
  );

  const LoanARMProps = {};
  const LoanARMLazy = useLazyComponent(LoanARM, <ARMSettingsSkeleton />, `LoanARM: ${JSON.stringify(LoanARMProps)}`);

  const LoanPaymentsDueProps = {};
  const LoanPaymentsDueLazy = useLazyComponent(
    LoanPaymentsDue,
    <LoanPaymentsDueSkeleton />,
    `LoanPaymentsDue: ${JSON.stringify(LoanPaymentsDueProps)}`,
  );

  // const LoginServicerPageProps = {};
  // const LoginServicerPageLazy = useLazyComponent(
  //   LoginServicerPage,
  //   <LoginServicerPageSkeleton />,
  //   `LoginServicerPage: ${JSON.stringify(LoginServicerPageProps)}`,
  // );

  // handle invalid servicer login hash key
  const { validServicerKey, fullAccessGNMA, hasProctorReportAccess } = useAuthContext();

  const openServicerLoginModal = useMemo(() => validServicerKey !== undefined && !validServicerKey, [validServicerKey]);
  if (openServicerLoginModal) {
    return <ErrorPage />;
  }

  return (
    <div
      className={[
        Styles.mainContent,
        ...(isKeyboardNavigator ? ['isKeyboardNavigator'] : []), // DO NOT USE Styles.isKeyboardNavigator
        ...(pathname === '/login' || pathname === '/login-servicer' ? [Styles.login] : []),
        ...(pathname === '/create-new-account' ? [Styles.createNewAccount] : []),
        ...(pathname === '/reset-password' ? [Styles.resetPassword] : []),
        ...(pathname === '/authenticate' ? [Styles.authenticate] : []),
        ...(pathname === '/error' ? [Styles.error] : []),
        // Judith Espinal - SQC-766
        ...(pathname === '/documents' ? [Styles.documents] : []),
      ].join(' ')}
    >
      {<CreateCommentModal />}
      {/* Hamburger menu to collapse LeftNavBar */}
      <details
        open={navBarOpen}
        title={(navBarOpen ? 'Hide' : 'Show') + ' Menu'}
        onToggle={(e) => setNavBarOpen(e.target['open'])}
      >
        <summary>
          <div aria-hidden></div>
        </summary>
      </details>
      <BorrowerEscrowAnalysisProvider>
        <LDCProvider>
          <LoanInformationProvider>
            <LeftNavBar />
          </LoanInformationProvider>
        </LDCProvider>
      </BorrowerEscrowAnalysisProvider>
      <main>
        <NavigationAlertProvider>
          <ProctorImportReportsProvider>
            {pathname !== '/error' && <LoanInfoHeader />}
            <GlobalSnackbar />
            <Routes>
              {/* All pages go here */}
              {/* NOTE: If a new page is being added to a category, be sure to update the respective
            categoryLinks useMemo() in LeftNavBar.tsx (e.g. loanActionsCategoryLinks) */}

              {/* Miscellaneous Routes */}
              <Route path='/' element={<NoLoginRedirect url='/select-loans' />} />
              <Route path='/authenticate' />
              <Route path='/error' element={<ErrorPage />} />
              <Route path='*' element={<NoPageFound />} />

              {/* Setup Category / Account-related */}
              <Route path='/login' element={<Login />} />
              <Route path='/login-servicer' element={<LoginServicerPage />} />
              <Route path='/reset-password' element={<ResetPasswordPage />} />
              <Route path='/create-new-account' element={<CreateAccountPage />} />
              <Route path='/select-loans' element={<SelectLoansPage />} />
              <Route path='/my-profile' element={<MyProfile />} />

              {/* Loan Information category */}
              <Route path='/loan-information' element={<LoanInformation />} />
              <Route path='/loan-history' element={<LoanHistoryPage />} />
              <Route path='/loan-buydown' element={<LoanBuydownPage />} />
              <Route path='/escrow-payment' element={<EscrowPaymentPage />} />
              <Route path='/payment-history' element={<BorrowerPayHistory />} />
              <Route path='/loan-escrow-accruals' element={<LoanServicingEscrowAccrualsPage />} />
              <Route path='/escrow-analysis' element={<EscrowAnalysisPage />} />
              <Route path='/documents' element={<DocumentsPage />} />
              {/* <Route path="/irs-1040-tax-info" element={(<IRS1040TaxInfoPage />)} /> */}
              <Route path='/loan-payments-due' element={<LoanPaymentsDueLazy {...LoanPaymentsDueProps} />} />
              <Route path='/communication' element={<CommunicationLazy {...CommunicationProps} />} />
              <Route path='/loan-arm' element={<LoanARMLazy {...LoanARMProps} />} />
              <Route path='/amortization-schedule' element={<AmortizationSchedulePage />} />
              <Route path='/loan-subsidy' element={<LoanSubsidyPage />} />
              <Route path='/loss-draft-repair' element={<LossDraftRepairPage />} />

              {/* Loan Actions category */}
              <Route path='/make-one-payment' element={<OneTimePaymentPage />} />
              <Route path='/recurring-payment' element={<AutopayPage />} />
              <Route path='/go-green' element={<GoGreenPage />} />
              <Route path='/change-contact-info' element={<ChangeContactInfoPage />} />
              <Route path='/pull-payoff' element={<PullPayoffPage />} />
              <Route path='/loan-default-codes' element={<LoanDefaultCodesPage />} />

              {/* Tools & Help category */}
              <Route path='/add-stop-code' element={<AddStopCodePage />} />
              <Route path='/add-edit-payee' element={<AddEditPayeePage />} />
              <Route path='/bulk-disbursement' element={<BulkDisbursementPage />} />
              <Route path='/upload-payees' element={<UploadPayeePage />} />
              <Route path='/payments-and-disbursements' element={<PaymentsAndDisbursements />} />
              <Route path='/pmi-servicing' element={<PmiServicingPage />} />
              <Route path='/mip-servicing' element={<MipServicingPage />} />
              <Route path='/midfirst-transfer' element={<MidFirstTransferPage />} />
              <Route path='/tax-payment' element={<TaxPaymentPage />} />
              <Route path='/borrower-login-audit' element={<BorrowerLoginAuditPage />} />
              {hasProctorReportAccess && (
                <Route path='/proctor-import-reports' element={<ProctorImportReportsPage />} />
              )}
              {fullAccessGNMA && <Route path='/gnma-reporting' element={<GNMAReportingPage />} />}
              <Route path='/glossary' element={<GlossaryPage />} />
              <Route path='/glossary/:index' element={<GlossaryPage />} />
              <Route path='/contact-us' element={<ExternalRedirect url={process.env.REACT_APP_SWMC_CONTACT_US} />} />
              <Route path='/privacy-policy' element={<ExternalRedirect url='https://www.swmc.com/privacy-policy' />} />
              <Route path='/disclaimers' element={<ExternalRedirect url='https://www.swmc.com/disclaimer' />} />
              <Route path='/e-sign-consent' element={<ExternalRedirect url='https://econsent.swmc.com/' />} />
              <Route path='/terms-and-conditions' element={<ExternalRedirect url='https://www.swmc.com/terms-and-conditions' />} />
              <Route path='/site-map' element={<ExternalRedirect url='https://www.swmc.com/swmc-site-map' />} />
              <Route path='/terms-and-conditions' element={<ExternalRedirect url='https://www.swmc.com/terms-and-conditions' />} />
              <Route path='/faq' element={<ExternalRedirect url='https://www.swmc.com/faq' />} />
            </Routes>
          </ProctorImportReportsProvider>
        </NavigationAlertProvider>
      </main>
      <Footer />
    </div>
  );
}

export default MainContent;
